import type { SkillBoard } from '../../data/skillBoards';

export interface SkillPaintState {
  progress: number;
  skillsVisible: number;
  scanY: number;
  brightness: number;
  active: boolean;
}

const IVORY = '#FFFBEE';
const IVORY_DIM = '#E8DFD0';
const PANEL = '#1C1A18';
const PANEL_MID = '#26221C';

function hexToRgb(hex: string) {
  const h = hex.replace('#', '');
  const n = parseInt(
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h,
    16,
  );
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function drawPremiumBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  fillRatio: number,
  color: string,
) {
  // Recessed track
  ctx.fillStyle = '#0A0908';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = 'rgba(232, 213, 181, 0.18)';
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

  const fw = Math.max(0, Math.min(1, fillRatio)) * (w - 4);
  if (fw > 1) {
    const grad = ctx.createLinearGradient(x, y, x + fw, y);
    grad.addColorStop(0, '#8B7355');
    grad.addColorStop(0.45, color);
    grad.addColorStop(1, '#F0E6D2');
    ctx.fillStyle = grad;
    ctx.shadowColor = 'rgba(201, 168, 108, 0.35)';
    ctx.shadowBlur = 8;
    ctx.fillRect(x + 2, y + 2, fw, h - 4);
    ctx.shadowBlur = 0;
  }
}

/**
 * Premium metal / champagne board — soft studio light, no neon.
 */
export function createSkillTexture(
  board: SkillBoard,
  state: SkillPaintState = {
    progress: board.progress,
    skillsVisible: board.skills.length,
    scanY: -1,
    brightness: 1,
    active: false,
  },
  logos: (CanvasImageSource | null)[] = [],
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const W = 512;
  const H = 768;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const { r, g, b } = hexToRgb(board.color);
  const accent = board.color;
  const br = THREE_CLAMP(state.brightness, 0.15, 1);

  // Soft charcoal panel with warm wash
  ctx.fillStyle = PANEL;
  ctx.fillRect(0, 0, W, H);
  const bg = ctx.createLinearGradient(0, 0, W * 0.3, H);
  bg.addColorStop(0, `rgba(${r},${g},${b},${0.08 * br})`);
  bg.addColorStop(0.5, PANEL_MID);
  bg.addColorStop(1, '#0E0C0A');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Subtle brushed grain
  ctx.globalAlpha = 0.04;
  for (let i = 0; i < 40; i++) {
    const yy = 40 + i * 17;
    ctx.strokeStyle = i % 2 === 0 ? '#E8D5B8' : '#000000';
    ctx.beginPath();
    ctx.moveTo(32, yy);
    ctx.lineTo(W - 32, yy + 1);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // Brass outer frame (premium edge, not neon glow)
  const frameGrad = ctx.createLinearGradient(0, 0, W, H);
  frameGrad.addColorStop(0, `rgba(230, 213, 184,${0.55 * br})`);
  frameGrad.addColorStop(0.5, `rgba(${r},${g},${b},${0.7 * br})`);
  frameGrad.addColorStop(1, `rgba(139, 115, 85,${0.5 * br})`);
  ctx.strokeStyle = frameGrad;
  ctx.lineWidth = 5;
  ctx.strokeRect(22, 22, W - 44, H - 44);

  // Inner hairline
  ctx.strokeStyle = `rgba(243, 237, 226,${0.12 * br})`;
  ctx.lineWidth = 1;
  ctx.strokeRect(34, 34, W - 68, H - 68);

  // Corner ticks — jewelry detail
  ctx.strokeStyle = `rgba(${r},${g},${b},${0.65 * br})`;
  ctx.lineWidth = 2;
  const c = 26;
  ctx.beginPath();
  ctx.moveTo(c, 48);
  ctx.lineTo(c, c);
  ctx.lineTo(48, c);
  ctx.moveTo(W - 48, c);
  ctx.lineTo(W - c, c);
  ctx.lineTo(W - c, 48);
  ctx.moveTo(c, H - 48);
  ctx.lineTo(c, H - c);
  ctx.lineTo(48, H - c);
  ctx.moveTo(W - 48, H - c);
  ctx.lineTo(W - c, H - c);
  ctx.lineTo(W - c, H - 48);
  ctx.stroke();

  // CORE SKILL pill — soft metal plate
  ctx.fillStyle = `rgba(${r},${g},${b},${0.14 * br})`;
  ctx.fillRect(40, 44, 118, 26);
  ctx.strokeStyle = `rgba(230, 213, 184,${0.35 * br})`;
  ctx.strokeRect(40.5, 44.5, 117, 25);
  ctx.fillStyle = IVORY_DIM;
  ctx.globalAlpha = br;
  ctx.font = '600 13px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('CORE SKILL', 52, 58);

  // Title — ivory + champagne metal
  ctx.fillStyle = IVORY;
  ctx.font = 'bold 52px Bebas Neue, Impact, sans-serif';
  ctx.fillText(board.title.toUpperCase(), 40, 118);
  ctx.fillStyle = accent;
  ctx.shadowColor = `rgba(${r},${g},${b},0.35)`;
  ctx.shadowBlur = state.active ? 10 : 3;
  ctx.font = 'bold 50px Bebas Neue, Impact, sans-serif';
  ctx.fillText(board.subtitle.toUpperCase(), 40, 166);
  ctx.shadowBlur = 0;

  // SKILL LEVEL
  ctx.fillStyle = IVORY_DIM;
  ctx.font = '600 14px Inter, system-ui, sans-serif';
  ctx.fillText('SKILL LEVEL', 40, 218);

  drawPremiumBar(ctx, 40, 236, 340, 18, state.progress / 100, accent);

  ctx.fillStyle = accent;
  ctx.font = 'bold 36px Bebas Neue, Impact, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(`${Math.round(state.progress)}%`, W - 40, 252);
  ctx.textAlign = 'left';

  // KEY TECHNOLOGIES
  ctx.fillStyle = IVORY_DIM;
  ctx.font = '600 14px Inter, system-ui, sans-serif';
  ctx.fillText('KEY TECHNOLOGIES', 40, 298);

  const startY = 328;
  const rowH = 62;
  board.skills.forEach((skill, i) => {
    const visible = i < state.skillsVisible;
    const alpha = visible ? Math.min(1, state.skillsVisible - i) * br : 0;
    if (alpha <= 0.02) return;
    const y = startY + i * rowH;
    ctx.globalAlpha = alpha;

    roundRect(ctx, 40, y - 10, W - 80, 54, 10);
    ctx.fillStyle = 'rgba(244, 239, 228, 0.06)';
    ctx.fill();

    ctx.fillStyle = IVORY;
    ctx.font = '600 24px Inter, system-ui, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(skill, 56, y + 17);

    const badge = 48;
    const bx = W - 48 - badge;
    const by = y - 6;
    roundRect(ctx, bx, by, badge, badge, 10);
    ctx.fillStyle = '#F7F3EA';
    ctx.fill();
    ctx.strokeStyle = 'rgba(139, 115, 85, 0.4)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const logo = logos[i];
    if (logo) {
      const pad = 7;
      ctx.drawImage(logo, bx + pad, by + pad, badge - pad * 2, badge - pad * 2);
    } else {
      ctx.fillStyle = accent;
      ctx.font = '700 14px Inter, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(skill.slice(0, 3).toUpperCase(), bx + badge / 2, by + badge / 2);
      ctx.textAlign = 'left';
    }
  });
  ctx.globalAlpha = 1;

  // Soft warm light sweep (studio, not neon scan)
  if (state.scanY >= 0 && state.scanY <= 1) {
    const sy = 28 + state.scanY * (H - 56);
    const scan = ctx.createLinearGradient(0, sy - 16, 0, sy + 16);
    scan.addColorStop(0, 'rgba(240, 220, 180, 0)');
    scan.addColorStop(0.5, `rgba(240, 220, 180,${0.18 * br})`);
    scan.addColorStop(1, 'rgba(240, 220, 180, 0)');
    ctx.fillStyle = scan;
    ctx.fillRect(24, sy - 16, W - 48, 32);
  }

  if (br < 0.95) {
    ctx.fillStyle = `rgba(8,6,4,${(1 - br) * 0.08})`;
    ctx.fillRect(0, 0, W, H);
  }

  return canvas;
}

function THREE_CLAMP(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
